import {Injectable, Inject} from 'ng-metadata/core';
import {getVid} from '../../../appConfig/appConfigForward';
import {getSearchScopesMap} from '../../../appConfig/appConfigForward';
import { MappingTablesCache } from '../../../mappingTablesCache/mappingTablesCache.service';
import {JwtUtilService} from '../../../security/jwtUtil.service';
import {StorageUtil} from "../../../infra/storageUtil.service";

const findKey= require('lodash/findKey');
const findIndex= require('lodash/findIndex');
const _get= require('lodash/get');
const RECENT_SCOPES_KEY_SUFFIX: string = "_RecentScopesKey";
const RECENT_SCOPES_MAX_LOCK_TIMEOUT: number = 100; //PRM-43582 - max timeout for safe exiting from waiting condition.

@Injectable()
export class TabsAndScopesSelectorService {
    private scopesUrl;
    private _isRemoteTab: boolean= false;
    private _isRecentScopesEnabled: boolean = false;
    private _recentScopesByTabMap = {};
    private RECENT_SCOPES_LIMIT: number = 3;
    private RECENT_SCOPES_REST_URL: string;
    private _recentScopesLock: boolean = false; //PRM-43582 - mutex to prevent calling addRecentScopeToSession and combineRecentScopeToSession at same time.

    constructor( private mtCache:MappingTablesCache,
                 private jwtUtilService : JwtUtilService,
                 private storageUtil: StorageUtil,
                 @Inject('$http') private $http : ng.IHttpService,
                 @Inject('$interval') private $interval: ng.IIntervalService,
                 @Inject('restBaseURLs') private restBaseURLs) {
        this.isRecentScopesEnabled = (this.mtCache.getTable(this.mtCache.tables.VIEW_PROPERTIES).filter((row) => row.source1 === getVid() && row.source2 === 'enable_recent_scopes' && row.target === 'true').length === 1);
        this.cleanOldRecentScopesMappingsFromLocalForage();
        this.RECENT_SCOPES_REST_URL = this.restBaseURLs.recentScopes;
    }

    public updateScopes() {
        this.scopesUrl= this.restBaseURLs.scopeListBaseURL+'/'+getVid()+'/scopes';
        return this.$http({
            method: 'GET',
            url: this.scopesUrl,
            params:{}
        });
    }

    public findTabByScopeName(scopeName: string): string{
        let searchScopesMap: Object= getSearchScopesMap();
        return findKey(searchScopesMap, (scopes: [string])=> findIndex(scopes, (scope)=>scope['scope-id'] == scopeName) > -1);
    }

    public updateIsRemoteTab(tab: string){
        let searchScopesMap: Object= getSearchScopesMap();
        let scopes= searchScopesMap[tab];
        this._isRemoteTab= findIndex(scopes, (scope:{types: string})=>scope.types.includes('remote')) > -1;
    }

    private addRecentScope(recentScopesMap, tab, scope){
        if(!recentScopesMap){
            recentScopesMap = {};
        }
        if(!recentScopesMap[tab]){
            recentScopesMap[tab] = [];
        }
        let scopeIndex = this.getScopeIndex(recentScopesMap, tab, scope)
        if(scopeIndex > -1){
            recentScopesMap[tab].splice(scopeIndex, 1);
        }else if(recentScopesMap[tab].length >= this.RECENT_SCOPES_LIMIT){
            recentScopesMap[tab].pop();
        }
        recentScopesMap[tab].unshift(scope);
    }

    public addRecentScopeToSession(tab, scope){
        if(!tab || !scope) return;
        if (this.recentScopesByTabMap && this.recentScopesByTabMap[tab] && this.recentScopesByTabMap[tab][0] == scope){
            //searching on same scope as last search - no need to update recent scopes
            return;
        }
        let interval = this.$interval(()=>{ //PRM-43582 - test-and-set - may continue only when data in DB was updated.
            if(!this._recentScopesLock){
                this.$interval.cancel(interval);
                this._recentScopesLock = true;
                if(this.isSignedIn()){
                    let recentScopesToAdd = {};
                    recentScopesToAdd[tab] = [scope];
                    this.saveRecentScopesToDB(recentScopesToAdd);
                }else{
                    this.getItemsFromLocalForage().then((recentScopesMap) => {
                        if(!recentScopesMap){
                            recentScopesMap = {};
                        }
                        this.addRecentScope(recentScopesMap, tab, scope);
                        this.setItemsToLocalForage(recentScopesMap);
                        this.recentScopesByTabMap = recentScopesMap;
                    }).finally(()=>{this._recentScopesLock = false;}); //PRM-43582 - finished writing, release lock.
                }
            }
        },100, RECENT_SCOPES_MAX_LOCK_TIMEOUT);

    }

    public combineRecentScopeToSession(userJwt){
        if(this.isSignedIn()){
            let interval = this.$interval(()=>{ //PRM-43582 - test-and-set - may continue only when data in DB was updated.
                if(!this._recentScopesLock){
                    this.$interval.cancel(interval);
                    this._recentScopesLock = true;
                    this.getItemsFromLocalForage(userJwt).then((recentScopesMap) => {
                        if(!recentScopesMap){
                            recentScopesMap = {};
                        }
                        this.saveRecentScopesToDB(recentScopesMap);
                    }),(()=>{this._recentScopesLock = false;}); //PRM-43582 - finished writing, release lock.
                }
            },100, RECENT_SCOPES_MAX_LOCK_TIMEOUT);
        }
    }

    private getScopeIndex(recentScopesMap, tab, scope){
        if(recentScopesMap[tab]){
            return recentScopesMap[tab].indexOf(scope);
        }
        return -1;
    }

    public getItemsFromLocalForage(jwt ?){
        let user = jwt;
        if(!user){
            user = this.jwtUtilService.getDecodedToken().user;
        }
        let localForageKey = user + RECENT_SCOPES_KEY_SUFFIX;
        return this.storageUtil.$localForage.getItem(localForageKey);
    }

    private setItemsToLocalForage(scopeMap){
        let user = this.jwtUtilService.getDecodedToken().user;
        let searchHistoryKey = user + RECENT_SCOPES_KEY_SUFFIX;
        this.storageUtil.$localForage.setItem(searchHistoryKey, scopeMap);
    }

    public cleanOldRecentScopesMappingsFromLocalForage(){
        let user = this.jwtUtilService.getDecodedToken().user;
        let currentLocalForageKey = user + RECENT_SCOPES_KEY_SUFFIX;
        this.storageUtil.$localForage.keys().then((allLocalForageKey) => {
            let oldLocalForageKey = allLocalForageKey.filter((key)=>{return ((key.indexOf(RECENT_SCOPES_KEY_SUFFIX) !== -1) && (key.indexOf(user) === -1))});
            for (var index in oldLocalForageKey) {
                this.storageUtil.$localForage.removeItem(oldLocalForageKey[index]);
            }
        });
    }

    public isSignedIn(){
        return this.jwtUtilService.isSignedIn();
    }

    private saveRecentScopesToDB(scopes){
        this.$http.post(this.RECENT_SCOPES_REST_URL,{'recentScopes': scopes, 'vid': getVid()}).then((response) => {
            let newRecentScopes = _get(response, 'data.recentScopes', {});
            this.setItemsToLocalForage(newRecentScopes);
            this.recentScopesByTabMap = newRecentScopes;
        }).finally(()=>{this._recentScopesLock = false;}); //PRM-43582 - finished writing, release lock.
    }

    private getRecentScopesFromDB(){
        return this.$http.get(this.RECENT_SCOPES_REST_URL,{'params': {'vid': getVid()}});
    }

    public getDefaultTab(): string {
        let searchScopesMap: Object = getSearchScopesMap();
        const tabOptions = Object.keys(searchScopesMap);
        return tabOptions.length > 0 ? tabOptions[0] : '';
    }

    public getDefaultSearchScopeForTab(tab: string): string {
        let searchScopesMap: Object= getSearchScopesMap();
        return searchScopesMap[tab][0]['scope-id'];
    }


    get isRemoteTab(){
        return this._isRemoteTab;
    }

    get isRecentScopesEnabled(): boolean {
        return this._isRecentScopesEnabled;
    }

    set isRecentScopesEnabled(value: boolean) {
        this._isRecentScopesEnabled = value;
    }

    get recentScopesByTabMap() {
        return this._recentScopesByTabMap;
    }

    set recentScopesByTabMap(value) {
        this._recentScopesByTabMap = value;
    }

}
//put here all angular1 modules the service is using
export default [];


// WEBPACK FOOTER //
// ./src/main/webapp/components/search/searchBar/tabsAndScopesSelector/tabsAndScopesSelector.service.ts